import React, { useEffect, useState } from 'react';
import { X, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { apiGet } from '../lib/api';
import { formatDate } from '../lib/format';
import { VendorBandBadge } from '../components/VendorBandBadge';

const TrendIcon = ({ a, b }) => {
  if (a == null || b == null) return <Minus size={13} className="text-slate-400" />;
  if (a > b * 1.02) return <TrendingUp size={13} className="text-emerald-600" />;
  if (a < b * 0.98) return <TrendingDown size={13} className="text-red-600" />;
  return <Minus size={13} className="text-slate-400" />;
};

const VendorDetailDrawer = ({ detail, onClose }) => {
  const s = detail.summary || {};
  const trend = detail.trend || [];
  const maxComp = Math.max(0.01, ...trend.map((t) => t.avg_composite || 0));
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="vendor-detail-drawer">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl animate-[slideIn_.2s_ease]">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="kpi-overline">Vendor Performance</div>
            <h3 className="font-display font-bold text-lg mt-0.5">{detail.vendor_name}</h3>
          </div>
          <button onClick={onClose} data-testid="vendor-detail-close" className="p-1.5 hover:bg-slate-100 rounded-sm">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-3">
            <div className="border border-slate-200 rounded-sm p-3">
              <div className="kpi-overline">Band</div>
              <div className="mt-1"><VendorBandBadge band={s.performance_band} score={s.avg_composite_score} /></div>
            </div>
            <div className="border border-slate-200 rounded-sm p-3">
              <div className="kpi-overline">Deliveries</div>
              <div className="font-display text-2xl font-extrabold mt-1">{s.total_deliveries || 0}</div>
            </div>
            <div className="border border-slate-200 rounded-sm p-3">
              <div className="kpi-overline">On-time</div>
              <div className="font-display text-2xl font-extrabold mt-1">{s.avg_on_time_pct ?? 0}%</div>
            </div>
            <div className="border border-slate-200 rounded-sm p-3">
              <div className="kpi-overline">Quality acceptance</div>
              <div className="font-display text-2xl font-extrabold mt-1">{s.avg_quality_acceptance_pct ?? 0}%</div>
            </div>
          </div>

          {trend.length > 0 && (
            <div>
              <div className="kpi-overline mb-2">Composite trend (last 6 months)</div>
              <div className="flex items-end gap-2 h-28 border-b border-slate-200 pb-1">
                {trend.map((t) => (
                  <div key={t.month} className="flex-1 flex flex-col items-center gap-1" title={`${t.month}: ${(t.avg_composite * 100).toFixed(0)}% · ${t.deliveries} deliveries`}>
                    <div className="w-full bg-[#1F7A6E] rounded-t-sm" style={{ height: `${(t.avg_composite / maxComp) * 90}px` }} />
                    <span className="text-[10px] text-slate-500">{t.month.slice(5)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="kpi-overline mb-2">Delivery scores</div>
            <table className="w-full dense-table">
              <thead><tr><th>GRN</th><th className="text-right">On-time</th><th className="text-right">Quality</th><th className="text-right">Docs</th><th className="text-right">Score</th><th>Date</th></tr></thead>
              <tbody>
                {(detail.deliveries || []).length === 0 && (
                  <tr><td colSpan="6" className="text-center py-6 text-slate-400 text-xs">No deliveries scored yet</td></tr>
                )}
                {(detail.deliveries || []).map((d) => (
                  <tr key={d.grn_id} data-testid={`vendor-delivery-${d.grn_id}`}>
                    <td className="text-xs font-mono">{d.grn_no || d.grn_id}</td>
                    <td className="text-right tabular-nums">{(d.on_time_score * 100).toFixed(0)}%</td>
                    <td className="text-right tabular-nums">{(d.quality_score * 100).toFixed(0)}%</td>
                    <td className="text-right tabular-nums">{(d.documentation_score * 100).toFixed(0)}%</td>
                    <td className="text-right tabular-nums font-display font-bold">{(d.composite_score * 100).toFixed(0)}%</td>
                    <td className="text-xs text-slate-500">{d.delivery_date ? formatDate(d.delivery_date) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export const VendorPerformance = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: 'avg_composite_score', dir: 'desc' });
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    apiGet('/procurement/vendor-ratings')
      .then((d) => setRows(d.vendors || []))
      .finally(() => setLoading(false));
  }, []);

  const openDetail = (vid) => {
    apiGet(`/procurement/vendor-ratings/${encodeURIComponent(vid)}`).then(setDetail);
  };

  const setSortKey = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  const sorted = [...rows].sort((a, b) => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    const av = a[sort.key], bv = b[sort.key];
    if (typeof av === 'number') return (av - bv) * dir;
    return String(av || '').localeCompare(String(bv || '')) * dir;
  });

  const Th = ({ k, children, right }) => (
    <th onClick={() => setSortKey(k)} className={`cursor-pointer select-none hover:text-slate-900 ${right ? 'text-right' : ''}`}>
      {children}{sort.key === k ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
    </th>
  );

  if (loading) return <div className="text-slate-400 text-sm py-8 text-center">Loading vendor performance…</div>;
  if (!rows.length) {
    return (
      <div data-testid="vendor-perf-empty" className="text-slate-400 text-sm py-12 text-center">
        No vendor deliveries scored yet. Scores are generated automatically once GRN inspections close.
      </div>
    );
  }

  return (
    <div data-testid="vendor-performance-panel">
      <p className="text-xs text-slate-500 mb-3">
        Composite = 40% on-time + 40% quality acceptance + 20% documentation. Click a vendor for delivery history & trend.
      </p>
      <table className="w-full dense-table">
        <thead><tr>
          <Th k="vendor_name">Vendor</Th>
          <th>Band</th>
          <Th k="total_deliveries" right>Deliveries</Th>
          <Th k="avg_composite_score" right>Composite</Th>
          <Th k="avg_on_time_pct" right>On-time %</Th>
          <Th k="avg_quality_acceptance_pct" right>Quality %</Th>
          <Th k="open_vendor_change_requests" right>Open VCRs</Th>
          <Th k="last_delivery_date">Last delivery</Th>
        </tr></thead>
        <tbody>
          {sorted.map((v) => (
            <tr
              key={v.vendor_id || v.vendor_name}
              data-testid={`vendor-row-${v.vendor_id || v.vendor_name}`}
              onClick={() => openDetail(v.vendor_id || v.vendor_name)}
              className="cursor-pointer hover:bg-slate-50"
            >
              <td className="font-display font-bold">{v.vendor_name}</td>
              <td><VendorBandBadge band={v.performance_band} score={v.avg_composite_score} /></td>
              <td className="text-right tabular-nums">{v.total_deliveries}</td>
              <td className="text-right tabular-nums font-display font-bold">{(v.avg_composite_score * 100).toFixed(0)}%</td>
              <td className="text-right tabular-nums">{v.avg_on_time_pct}%</td>
              <td className="text-right tabular-nums">{v.avg_quality_acceptance_pct}%</td>
              <td className="text-right tabular-nums">
                {v.open_vendor_change_requests > 0
                  ? <span className="text-red-600 font-semibold">{v.open_vendor_change_requests}</span>
                  : <span className="text-slate-400">0</span>}
              </td>
              <td className="text-xs text-slate-500">{v.last_delivery_date ? formatDate(v.last_delivery_date) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {detail && <VendorDetailDrawer detail={detail} onClose={() => setDetail(null)} />}
    </div>
  );
};

export default VendorPerformance;
